import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';

const MARGIN = { top: 10, right: 14, left: 46 };
const BRUSH_HEIGHT = 44;
// espacio entre plot y brush (incluye etiquetas del eje X) y bajo el brush (fechas)
const X_LABEL_SPACE = 22;
const BRUSH_LABEL_SPACE = 16;

const COLOR_SERIES = '#1976d2';
const COLOR_SMOOTHED = '#7b1fa2';
const COLOR_EPISODE = 'rgba(211, 47, 47, 0.18)';
const COLOR_EPISODE_BORDER = 'rgba(211, 47, 47, 0.55)';
const COLOR_DISCARDED = 'rgba(120, 120, 120, 0.22)';
const COLOR_THRESHOLD = '#d32f2f';
const COLOR_EXIT = '#ef6c00';
const COLOR_BAND_HIGH = '#c2185b';

function fmtTime(ms) {
  const d = new Date(ms);
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getUTCDate())}/${p(d.getUTCMonth() + 1)} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
}

function fmtShort(ms) {
  const d = new Date(ms);
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
}

function niceRound(value, range) {
  if (!Number.isFinite(value)) return value;
  if (range >= 200) return Math.round(value);
  if (range >= 20) return Math.round(value * 2) / 2;
  if (range >= 2) return Math.round(value * 10) / 10;
  return Math.round(value * 100) / 100;
}

/** Decima la serie a ~maxPoints conservando forma (min y max por tramo). */
function decimate(points, maxPoints) {
  if (points.length <= maxPoints) return points;
  const bucket = Math.ceil(points.length / (maxPoints / 2));
  const out = [];
  for (let i = 0; i < points.length; i += bucket) {
    const slice = points.slice(i, i + bucket);
    let lo = slice[0];
    let hi = slice[0];
    for (const p of slice) {
      if (p.value < lo.value) lo = p;
      if (p.value > hi.value) hi = p;
    }
    if (lo.ms <= hi.ms) {
      out.push(lo);
      if (hi !== lo) out.push(hi);
    } else {
      out.push(hi);
      if (lo !== hi) out.push(lo);
    }
  }
  return out;
}

/**
 * Gráfico calibrador de episodios: serie temporal con umbrales arrastrables,
 * episodios sombreados y brush de ventana para simular en distintos tramos.
 */
export default function EpisodeCalibratorChart({
  points, // serie cruda completa [{ms, value}]
  smoothed, // valores suavizados alineados con filledPoints
  filledPoints, // puntos tras rellenar huecos (si missingAsZero)
  episodes, // episodios detectados [{startMs, endMs}]
  discarded, // episodios descartados por duración mínima
  cfg,
  onCfgChange, // (patch) => void, al arrastrar umbrales
  height = 300,
}) {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(800);
  const [win, setWin] = useState(null); // [startMs, endMs]
  const [hover, setHover] = useState(null); // {ms, value, x, y}
  const dragRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w && w > 100) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const seriesForDisplay = useMemo(
    () => (filledPoints?.length ? filledPoints : points),
    [filledPoints, points]
  );

  const domain = useMemo(() => {
    if (!seriesForDisplay.length) return null;
    let minMs = Infinity;
    let maxMs = -Infinity;
    for (const p of seriesForDisplay) {
      if (p.ms < minMs) minMs = p.ms;
      if (p.ms > maxMs) maxMs = p.ms;
    }
    return [minMs, maxMs + 60000];
  }, [seriesForDisplay]);

  // Ventana inicial: serie completa; se resetea si cambia la serie
  useEffect(() => {
    if (domain) setWin(domain);
  }, [domain]);

  const yDomain = useMemo(() => {
    if (!seriesForDisplay.length) return [0, 1];
    let max = -Infinity;
    let min = 0;
    for (const p of seriesForDisplay) {
      if (p.value > max) max = p.value;
      if (p.value < min) min = p.value;
    }
    const t = Number(cfg.threshold);
    const th = Number(cfg.thresholdHigh);
    const te = Number(cfg.exitThreshold);
    if (Number.isFinite(t)) max = Math.max(max, t);
    if (Number.isFinite(th)) max = Math.max(max, th);
    if (Number.isFinite(te)) max = Math.max(max, te);
    if (!Number.isFinite(max) || max <= min) max = min + 1;
    return [min, max * 1.08];
  }, [seriesForDisplay, cfg.threshold, cfg.thresholdHigh, cfg.exitThreshold]);

  const plotW = Math.max(50, width - MARGIN.left - MARGIN.right);
  const plotH = Math.max(
    50,
    height - MARGIN.top - X_LABEL_SPACE - BRUSH_HEIGHT - BRUSH_LABEL_SPACE
  );
  const brushY = MARGIN.top + plotH + X_LABEL_SPACE;

  const [winStart, winEnd] = win || domain || [0, 1];
  const xScale = useCallback(
    (ms) => ((ms - winStart) / Math.max(1, winEnd - winStart)) * plotW,
    [winStart, winEnd, plotW]
  );
  const yScale = useCallback(
    (v) => plotH - ((v - yDomain[0]) / Math.max(1e-9, yDomain[1] - yDomain[0])) * plotH,
    [plotH, yDomain]
  );
  const yFromPixel = useCallback(
    (py) => yDomain[0] + ((plotH - py) / plotH) * (yDomain[1] - yDomain[0]),
    [plotH, yDomain]
  );
  const brushXScale = useCallback(
    (ms) => (domain ? ((ms - domain[0]) / Math.max(1, domain[1] - domain[0])) * plotW : 0),
    [domain, plotW]
  );
  const brushMsFromPixel = useCallback(
    (px) => (domain ? domain[0] + (px / plotW) * (domain[1] - domain[0]) : 0),
    [domain, plotW]
  );

  const visiblePoints = useMemo(() => {
    const inWin = seriesForDisplay.filter((p) => p.ms >= winStart - 60000 && p.ms <= winEnd + 60000);
    return decimate(inWin, Math.max(300, plotW * 2));
  }, [seriesForDisplay, winStart, winEnd, plotW]);

  const visibleSmoothed = useMemo(() => {
    if (!smoothed?.length || !filledPoints?.length) return [];
    if (!cfg.smoothing || cfg.smoothing === 'none') return [];
    const paired = filledPoints.map((p, i) => ({ ms: p.ms, value: smoothed[i] }));
    const inWin = paired.filter((p) => p.ms >= winStart - 60000 && p.ms <= winEnd + 60000);
    return decimate(inWin, Math.max(300, plotW * 2));
  }, [smoothed, filledPoints, cfg.smoothing, winStart, winEnd, plotW]);

  const seriesPath = useMemo(() => {
    if (!visiblePoints.length) return '';
    return visiblePoints
      .map((p, i) => `${i === 0 ? 'M' : 'L'}${xScale(p.ms).toFixed(1)},${yScale(p.value).toFixed(1)}`)
      .join('');
  }, [visiblePoints, xScale, yScale]);

  const smoothedPath = useMemo(() => {
    if (!visibleSmoothed.length) return '';
    return visibleSmoothed
      .map((p, i) => `${i === 0 ? 'M' : 'L'}${xScale(p.ms).toFixed(1)},${yScale(p.value).toFixed(1)}`)
      .join('');
  }, [visibleSmoothed, xScale, yScale]);

  const brushPath = useMemo(() => {
    if (!seriesForDisplay.length || !domain) return '';
    const dec = decimate(seriesForDisplay, Math.max(200, plotW));
    const bh = BRUSH_HEIGHT - 6;
    let max = -Infinity;
    let min = 0;
    for (const p of seriesForDisplay) {
      if (p.value > max) max = p.value;
      if (p.value < min) min = p.value;
    }
    if (max <= min) max = min + 1;
    const by = (v) => bh - ((v - min) / (max - min)) * bh + 3;
    return dec
      .map((p, i) => `${i === 0 ? 'M' : 'L'}${brushXScale(p.ms).toFixed(1)},${by(p.value).toFixed(1)}`)
      .join('');
  }, [seriesForDisplay, domain, plotW, brushXScale]);

  // Ticks del eje X de la ventana
  const xTicks = useMemo(() => {
    const span = winEnd - winStart;
    const target = Math.max(3, Math.floor(plotW / 110));
    const step = span / target;
    const ticks = [];
    for (let i = 0; i <= target; i += 1) {
      ticks.push(winStart + step * i);
    }
    return ticks;
  }, [winStart, winEnd, plotW]);

  const yTicks = useMemo(() => {
    const [lo, hi] = yDomain;
    const target = 5;
    const step = (hi - lo) / target;
    return Array.from({ length: target + 1 }, (_, i) => lo + step * i);
  }, [yDomain]);

  const isBand = cfg.condition === 'insideBand' || cfg.condition === 'outsideBand';
  const tVal = Number(cfg.threshold);
  const tHighVal = Number(cfg.thresholdHigh);
  const tExitVal = Number(cfg.exitThreshold);
  const showExit = !isBand && cfg.exitThreshold !== '' && Number.isFinite(tExitVal);

  const clampMs = useCallback(
    (ms) => (domain ? Math.min(domain[1], Math.max(domain[0], ms)) : ms),
    [domain]
  );

  // ── Interacción ─────────────────────────────────────────────────────────
  const handlePointerDown = (e, kind) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const rect = containerRef.current.getBoundingClientRect();
    dragRef.current = {
      kind,
      startX: e.clientX - rect.left,
      startWin: [winStart, winEnd],
    };
  };

  const handlePointerMove = (e) => {
    const rect = containerRef.current.getBoundingClientRect();
    const px = e.clientX - rect.left - MARGIN.left;
    const py = e.clientY - rect.top - MARGIN.top;
    const drag = dragRef.current;

    if (!drag) {
      // hover tooltip sobre el gráfico principal
      if (px >= 0 && px <= plotW && py >= 0 && py <= plotH && visiblePoints.length) {
        const ms = winStart + (px / plotW) * (winEnd - winStart);
        let nearest = visiblePoints[0];
        for (const p of visiblePoints) {
          if (Math.abs(p.ms - ms) < Math.abs(nearest.ms - ms)) nearest = p;
        }
        setHover({ ms: nearest.ms, value: nearest.value, x: xScale(nearest.ms), y: yScale(nearest.value) });
      } else {
        setHover(null);
      }
      return;
    }

    const range = yDomain[1] - yDomain[0];
    if (drag.kind === 'threshold' || drag.kind === 'exitThreshold' || drag.kind === 'thresholdHigh') {
      const v = niceRound(yFromPixel(Math.min(plotH, Math.max(0, py))), range);
      onCfgChange({ [drag.kind]: v });
      return;
    }

    const dxMs = ((e.clientX - rect.left - drag.startX) / plotW) * (domain[1] - domain[0]);
    const [s0, e0] = drag.startWin;
    if (drag.kind === 'brushMove') {
      let s = s0 + dxMs;
      let en = e0 + dxMs;
      const span = e0 - s0;
      if (s < domain[0]) { s = domain[0]; en = s + span; }
      if (en > domain[1]) { en = domain[1]; s = en - span; }
      setWin([s, en]);
    } else if (drag.kind === 'brushLeft') {
      setWin([Math.min(clampMs(s0 + dxMs), e0 - 60000), e0]);
    } else if (drag.kind === 'brushRight') {
      setWin([s0, Math.max(clampMs(e0 + dxMs), s0 + 60000)]);
    } else if (drag.kind === 'pan') {
      const dxWinMs = ((drag.startX - (e.clientX - rect.left)) / plotW) * (e0 - s0);
      let s = s0 + dxWinMs;
      let en = e0 + dxWinMs;
      const span = e0 - s0;
      if (s < domain[0]) { s = domain[0]; en = s + span; }
      if (en > domain[1]) { en = domain[1]; s = en - span; }
      setWin([s, en]);
    } else if (drag.kind === 'brushNew') {
      const ms0 = brushMsFromPixel(drag.startX - MARGIN.left);
      const ms1 = brushMsFromPixel(px);
      const s = clampMs(Math.min(ms0, ms1));
      const en = clampMs(Math.max(ms0, ms1));
      if (en - s > 60000) setWin([s, en]);
    }
  };

  const handlePointerUp = () => {
    dragRef.current = null;
  };

  const renderEpisodeRects = (list, fill, stroke) =>
    list
      .filter((ep) => ep.endMs > winStart && ep.startMs < winEnd)
      .map((ep, i) => {
        const x0 = Math.max(0, xScale(ep.startMs));
        const x1 = Math.min(plotW, xScale(ep.endMs));
        if (x1 - x0 < 0.5) return null;
        return (
          <rect
            key={`${fill}-${i}`}
            x={x0}
            y={0}
            width={x1 - x0}
            height={plotH}
            fill={fill}
            stroke={stroke || 'none'}
            strokeWidth={stroke ? 1 : 0}
          />
        );
      });

  const thresholdLine = (value, color, kind, label, dashed) => {
    if (!Number.isFinite(value)) return null;
    const y = yScale(value);
    if (y < -10 || y > plotH + 10) return null;
    return (
      <g key={kind}>
        <line x1={0} x2={plotW} y1={y} y2={y} stroke={color} strokeWidth={1.5} strokeDasharray={dashed ? '6 4' : undefined} />
        {/* zona de agarre invisible más ancha */}
        <line
          x1={0}
          x2={plotW}
          y1={y}
          y2={y}
          stroke="transparent"
          strokeWidth={14}
          style={{ cursor: 'ns-resize' }}
          onPointerDown={(e) => handlePointerDown(e, kind)}
        />
        <rect x={plotW - 118} y={y - 9} width={116} height={16} rx={3} fill="white" opacity={0.85} />
        <text x={plotW - 6} y={y + 3} fontSize={10} fill={color} textAnchor="end" style={{ userSelect: 'none', pointerEvents: 'none' }}>
          {label}: {value}
        </text>
      </g>
    );
  };

  if (!points.length) {
    return (
      <Box
        ref={containerRef}
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.50',
          borderRadius: 1,
          border: '1px dashed',
          borderColor: 'divider',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Sin datos de serie para calibrar. Verifica columnas y filtros del dataset.
        </Typography>
      </Box>
    );
  }

  const winX0 = brushXScale(winStart);
  const winX1 = brushXScale(winEnd);

  return (
    <Box ref={containerRef} sx={{ width: '100%', userSelect: 'none' }}>
      <svg
        width={width}
        height={height}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={() => {
          setHover(null);
          dragRef.current = null;
        }}
        style={{ display: 'block', touchAction: 'none' }}
      >
        {/* ── Gráfico principal ── */}
        <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
          <rect
            x={0}
            y={0}
            width={plotW}
            height={plotH}
            fill="#fafafa"
            stroke="#e0e0e0"
            style={{ cursor: 'grab' }}
            onPointerDown={(e) => handlePointerDown(e, 'pan')}
          />
          {yTicks.map((t, i) => (
            <g key={`yt-${i}`}>
              <line x1={0} x2={plotW} y1={yScale(t)} y2={yScale(t)} stroke="#eee" />
              <text x={-8} y={yScale(t) + 3} fontSize={10} fill="#888" textAnchor="end">
                {Math.abs(t) >= 100 ? Math.round(t) : Number(t.toFixed(1))}
              </text>
            </g>
          ))}
          {xTicks.map((t, i) => (
            <text key={`xt-${i}`} x={xScale(t)} y={plotH + 14} fontSize={10} fill="#888" textAnchor="middle">
              {winEnd - winStart > 86400000 * 2 ? fmtTime(t) : fmtShort(t)}
            </text>
          ))}

          {/* episodios */}
          <g style={{ pointerEvents: 'none' }}>
            {renderEpisodeRects(discarded || [], COLOR_DISCARDED)}
            {renderEpisodeRects(episodes || [], COLOR_EPISODE, COLOR_EPISODE_BORDER)}
          </g>

          {/* serie */}
          <path d={seriesPath} fill="none" stroke={COLOR_SERIES} strokeWidth={1.4} style={{ pointerEvents: 'none' }} />
          {smoothedPath && (
            <path
              d={smoothedPath}
              fill="none"
              stroke={COLOR_SMOOTHED}
              strokeWidth={1.4}
              strokeDasharray="4 3"
              style={{ pointerEvents: 'none' }}
            />
          )}

          {/* umbrales arrastrables */}
          {thresholdLine(tVal, COLOR_THRESHOLD, 'threshold', isBand ? 'Umbral A' : 'Umbral', false)}
          {isBand && thresholdLine(tHighVal, COLOR_BAND_HIGH, 'thresholdHigh', 'Umbral B', false)}
          {showExit && thresholdLine(tExitVal, COLOR_EXIT, 'exitThreshold', 'Salida', true)}

          {/* tooltip */}
          {hover && (
            <g style={{ pointerEvents: 'none' }}>
              <line x1={hover.x} x2={hover.x} y1={0} y2={plotH} stroke="#bbb" strokeDasharray="3 3" />
              <circle cx={hover.x} cy={hover.y} r={3} fill={COLOR_SERIES} />
              <rect
                x={Math.min(hover.x + 6, plotW - 130)}
                y={Math.max(2, hover.y - 34)}
                width={124}
                height={30}
                rx={4}
                fill="rgba(33,33,33,0.85)"
              />
              <text x={Math.min(hover.x + 12, plotW - 124)} y={Math.max(15, hover.y - 21)} fontSize={10} fill="#fff">
                {fmtTime(hover.ms)}
              </text>
              <text x={Math.min(hover.x + 12, plotW - 124)} y={Math.max(28, hover.y - 8)} fontSize={10} fill="#90caf9">
                valor: {Number(hover.value.toFixed ? hover.value.toFixed(2) : hover.value)}
              </text>
            </g>
          )}
        </g>

        {/* ── Brush (vista completa) ── */}
        <g transform={`translate(${MARGIN.left},${brushY})`}>
          <rect
            x={0}
            y={0}
            width={plotW}
            height={BRUSH_HEIGHT}
            fill="#f5f5f5"
            stroke="#e0e0e0"
            style={{ cursor: 'crosshair' }}
            onPointerDown={(e) => handlePointerDown(e, 'brushNew')}
          />
          <path d={brushPath} fill="none" stroke="#90a4ae" strokeWidth={1} style={{ pointerEvents: 'none' }} />
          {/* episodios en overview */}
          <g style={{ pointerEvents: 'none' }}>
            {(episodes || []).map((ep, i) => (
              <rect
                key={`bep-${i}`}
                x={brushXScale(ep.startMs)}
                y={0}
                width={Math.max(1, brushXScale(ep.endMs) - brushXScale(ep.startMs))}
                height={BRUSH_HEIGHT}
                fill={COLOR_EPISODE}
              />
            ))}
          </g>
          {/* ventana */}
          <rect
            x={winX0}
            y={0}
            width={Math.max(2, winX1 - winX0)}
            height={BRUSH_HEIGHT}
            fill="rgba(25, 118, 210, 0.12)"
            stroke="#1976d2"
            style={{ cursor: 'move' }}
            onPointerDown={(e) => handlePointerDown(e, 'brushMove')}
          />
          <rect
            x={winX0 - 4}
            y={0}
            width={8}
            height={BRUSH_HEIGHT}
            fill="transparent"
            style={{ cursor: 'ew-resize' }}
            onPointerDown={(e) => handlePointerDown(e, 'brushLeft')}
          />
          <rect
            x={winX1 - 4}
            y={0}
            width={8}
            height={BRUSH_HEIGHT}
            fill="transparent"
            style={{ cursor: 'ew-resize' }}
            onPointerDown={(e) => handlePointerDown(e, 'brushRight')}
          />
          <text x={0} y={BRUSH_HEIGHT + 12} fontSize={9} fill="#999">
            {domain ? fmtTime(domain[0]) : ''}
          </text>
          <text x={plotW} y={BRUSH_HEIGHT + 12} fontSize={9} fill="#999" textAnchor="end">
            {domain ? fmtTime(domain[1]) : ''}
          </text>
        </g>
      </svg>
    </Box>
  );
}
